import { texts } from "@utils/textMessages"
import { log } from "@utils/logger"
import { expandWhere, flattenWhere } from "@utils/search"
import Session, { useSessionContext } from "supertokens-auth-react/recipe/session"
import { appInfo, dvcApiDomain } from "config/appInfo"
import useSWR, { mutate } from "swr"
import { getQueryFromFilters } from "@components/Listings/ListingsFilter/store"

export type User = {
    username?: string
    first_name?: string
    emails?: string[]
    email_notification?: boolean
    isThirdParty: boolean
}
const baseURL = dvcApiDomain

export async function getUser() {
    let user
    let error
    let token
    try {
        if (await Session.doesSessionExist()) {
            user = await fetch(`${appInfo.apiDomain}/api/user`, { headers: { ...(typeof window === "undefined" && { "User-Agent": "dvc-client" }) } }).then((res) => res.json())

            const accessTokenPayload = await Session.getAccessTokenPayloadSecurely()
            if (accessTokenPayload.sub !== undefined) {
                token = await Session.getAccessToken()
            } else {
                // This branch is only required if there are valid access tokens created before the update
                // It can be removed after the validity period ends
                token = accessTokenPayload.jwt
            }
        } else {
            error = texts.notAuthenticated
        }
    } catch (err) {
        error = err.toString()
    }
    if (error) {
        return Promise.reject(error)
    }
    return { token, user }
}

export function useUser() {
    const { loading } = useSessionContext()
    const swrRes = useSWR<{ user: User; token: string }>(loading ? null : `${appInfo.apiDomain}/api/user`, getUser)
    return { ...swrRes, isAuthenticated: !!swrRes?.data?.user, user: swrRes.data?.user, isLoading: swrRes.isLoading || loading }
}
const searches = [
    {
        search_query: {
            where: {}
        },
        created_at: "31be4c82-ea76-4d20-91f1-70883d6c272b",
        frequency: "daily",
        username: "supertokens_2e6cfdc6-2e65-4d3c-9d61-0f00003223fa",
        name: "Second",
        should_notify: 1
    },
    {
        search_query: {
            where: {}
        },
        created_at: "36595337-7876-4a50-a967-0c4588a8e64d",
        frequency: "2x-daily",
        username: "supertokens_2e6cfdc6-2e65-4d3c-9d61-0f00003223fa",
        name: "dd",
        should_notify: 1
    },
    {
        search_query: {
            where: {
                price: {
                    lte: 40000
                },
                origin: {
                    in: ["dvcshop"]
                },
                points_on_contract: {
                    gte: 20,
                    lte: 255
                },
                use_month: {
                    in: ["April"]
                },
                stars: {
                    gte: 4
                },
                resort: {
                    short_name: {
                        in: ["BoardWalk Villas"]
                    }
                },
                price_per_point: {
                    lte: 200
                },
                and: [
                    {
                        or: [
                            {
                                listing_status: {
                                    contains: "Double Points"
                                }
                            }
                        ]
                    },
                    {
                        or: [
                            {
                                listing_status: {
                                    notContains: "Sold"
                                }
                            }
                        ]
                    }
                ]
            },
            sort: "stars:asc"
        },
        created_at: "597f240a-a2f0-4ac1-891e-3d31f8619fa3",
        frequency: "2x-daily",
        username: "supertokens_2e6cfdc6-2e65-4d3c-9d61-0f00003223fa",
        name: "new",
        should_notify: 1
    },
    {
        search_query: {
            where: {}
        },
        created_at: "a463808a-8b8e-4974-b232-de94ff37f6b4",
        frequency: "2x-daily",
        username: "supertokens_2e6cfdc6-2e65-4d3c-9d61-0f00003223fa",
        name: "alert 3",
        should_notify: 1
    }
]

function normalizeSavedSearch(s: any) {
    const where = { ...flattenWhere(s?.search_query?.where) }
    const sort = String(s?.search_query?.sort || "")
    let query = s?.search_query?.query
    //const resortIds = where?.resort?.short_name_in?.map((r) => resorts.find((resort) => resort.short_name === r)?.id.toString()) || []

    if (!query) {
        query = getQueryFromFilters({
            ...where,
            sort
        })
    }
    return { ...s, search_query: { ...s?.search_query, query, where, sort } }
}
async function fetchUserSearches() {
    const { token, user } = await getUser()
    try {
        const searches = await fetch(`${baseURL}/listings/searches/${user.username}`, {
            headers: {
                Authorization: token,
                ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
            }
        }).then((res) => res.json())
        return Array.isArray(searches) ? searches.map((s) => normalizeSavedSearch(s)) : []
    } catch (e: any) {
        log(e)
        return []
    }
}

export const useUserSearches = () => {
    const { user } = useUser()

    const { data, isLoading, mutate } = useSWR(user?.username ? `${baseURL}/listings/searches?u=${user.username}` : null, fetchUserSearches)

    const searches = Array.isArray(data) ? data : []

    return { searches, isLoading, mutate }
}

export async function saveUserSearches(body) {
    const expandedBody = { ...body, search_query: { ...body?.search_query, where: { ...expandWhere(body?.search_query?.where) } } }
    try {
        const { token, user } = await getUser()
        const url = `${baseURL}/listings/searches?u=${user.username}`
        return await fetch(url, {
            method: "POST",
            headers: {
                Authorization: token,
                "Content-Type": "application/json",
                ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
            },
            body: JSON.stringify(expandedBody)
        })
            .then((res) => res.json())
            .finally(() => {
                mutate(url)
            })
    } catch (err) {
        log(err)
        throw new Error(texts.somethingWentWrong)
    }
}

export async function updateUserSearches(body) {
    const expandedBody = { ...body, search_query: { ...body?.search_query, where: { ...expandWhere(body?.search_query?.where) } } }
    try {
        const { token, user } = await getUser()
        const url = `${baseURL}/listings/searches?u=${user.username}`
        return await fetch(url, {
            method: "PUT",
            headers: {
                Authorization: token,
                "Content-Type": "application/json",
                ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
            },
            body: JSON.stringify(expandedBody)
        })
            .then((res) => res.json())
            .finally(() => {
                mutate(url)
            })
    } catch (err) {
        log(err)
        throw new Error(texts.somethingWentWrong)
    }
}

export async function deleteUserSearches(created_at: string) {
    return await fetch(`${baseURL}/listings/searches/object/${created_at}`, {
        method: "DELETE",
        headers: {
            ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
        }
    }).then((res) => res.json())
}

export async function getSearchByCreatedAt(created_at: string) {
    const search = await fetch(`${baseURL}/listings/searches/search/${created_at}`, { headers: { ...(typeof window === "undefined" && { "User-Agent": "dvc-client" }) } }).then(
        (res) => res.json()
    )
    return normalizeSavedSearch(search)
}

export function updateUserProps(newProps: Partial<{ email_notification: boolean }>) {
    return fetch(`${appInfo.apiDomain}/api/user`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
        },
        body: JSON.stringify(newProps)
    })
}
